import { useState } from "react";
import { StoredFilterItem } from "../../../entities/Filter/StoredFiltersResponse";
import CompanyService from "../../../services/CompanyService";
import StoredFiltersService, {
  STORED_FILTER_NAME_MAX_LENGTH
} from "../../../services/StoredFiltersService";
import { TranslationService } from "../../../services/TranslationService";
import { getError } from "../../../utils/RequestUtils";
import ModalService from "../../shared/bootstrap/modal/Modal";
import { ToastService } from "../../shared/bootstrap/Toast";
import Dropdown from "../../shared/components/Dropdown";
import { CheckBoxEditor, TextEditor } from "../../shared/components/Editors";
import { FloatingPanelFooter } from "../../shared/components/FloatingPanelFooter";
import FloatingPanelService from "../../shared/FloatingPanel";
import {
  RequiredManager,
  ValidationMessage
} from "../../shared/RequieredManager";
import {
  GROUPING_REPORT_ID,
  isDefaultGrouping,
  isOwnPrivateGrouping,
  withGroupingDefault
} from "./SavedGroupingUtils";

const StoredGroupingPanel = ({
  grouping,
  groupings,
  isNew,
  onSaved,
  onDeleted
}: {
  grouping: Partial<StoredFilterItem>;
  groupings: StoredFilterItem[];
  isNew: boolean;
  onSaved: (id: number) => void;
  onDeleted: () => void;
}) => {
  const { translate } = TranslationService;
  const isInvited = CompanyService.getIsInvited();
  const userId = parseInt(CompanyService.getUserid());
  const [name, setName] = useState(grouping.Name);
  const [privateState, setPrivateState] = useState<boolean>(
    isInvited ? true : (grouping.Private ?? false)
  );
  const [defaultState, setDefaultState] = useState<boolean>(
    isDefaultGrouping(grouping.Filter)
  );
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const requiredManager = new RequiredManager();
  const isPrivate = isInvited || privateState;

  const options = [
    { value: true, text: translate.OnlyMe },
    { value: false, text: translate.AllUsers }
  ];

  // Only one private grouping per user can be opened by default.
  const clearOtherDefaults = async (savedId: number) => {
    const others = groupings.filter(
      (x) =>
        x.StoredFilterID !== savedId &&
        isOwnPrivateGrouping(x, userId) &&
        isDefaultGrouping(x.Filter)
    );
    for (const other of others) {
      await StoredFiltersService.update({
        item: {
          StoredFilterID: other.StoredFilterID,
          Filter: withGroupingDefault(other.Filter, false)
        }
      });
    }
  };

  const save = async () => {
    if (!requiredManager.validate() || !name || !grouping.Filter) {
      ToastService.showToast(
        translate.MissingRequiredFields,
        undefined,
        "danger"
      );
      return;
    }
    if (name.length < 3) {
      ToastService.showToast(translate.ErrorNameTooShort, undefined, "danger");
      return;
    }
    // A grouping made public can't stay as the default one.
    const isDefault = isPrivate && defaultState;
    const filter = withGroupingDefault(grouping.Filter, isDefault);
    setIsSaving(true);
    try {
      const result = await StoredFiltersService.set({
        item: {
          Report: GROUPING_REPORT_ID,
          Name: name,
          UserID: grouping.UserID || userId,
          Private: isPrivate,
          Filter: filter,
          StoredFilterID: grouping.StoredFilterID
        }
      });
      if (result instanceof Error) {
        const error = getError(result);
        ToastService.showToast(
          error.message === "Fail-Name"
            ? translate.FieldAlreadyCreated
            : translate.ErrorProcessingRequest,
          undefined,
          "danger"
        );
        return;
      }
      // storedfilter/set ignores Filter when editing an existing item.
      if (!isNew && filter !== grouping.Filter) {
        const updateResult = await StoredFiltersService.update({
          item: { StoredFilterID: result.Id, Filter: filter }
        });
        if (updateResult instanceof Error) {
          ToastService.showToast(
            translate.ErrorProcessingRequest,
            undefined,
            "danger"
          );
          return;
        }
      }
      if (isDefault) {
        await clearOtherDefaults(result.Id);
      }
      onSaved(result.Id);
      FloatingPanelService.hidePanel();
    } finally {
      setIsSaving(false);
    }
  };

  const deleteGrouping = async () => {
    if (!grouping.StoredFilterID) {
      return;
    }
    setIsDeleting(true);
    const result = await StoredFiltersService.delete({
      id: grouping.StoredFilterID
    });
    setIsDeleting(false);
    if (result instanceof Error) {
      ToastService.showToast(
        translate.ErrorProcessingRequest,
        undefined,
        "danger"
      );
      return;
    }
    onDeleted();
    FloatingPanelService.hidePanel();
  };

  const confirmDelete = () => {
    ModalService.showDefaultModal({
      acceptButtonClassName: "btn btn-danger",
      acceptButtonLabel: translate.Yes,
      message: translate.SureDeleteGrouping,
      title: translate.Delete,
      onAcceptClick: deleteGrouping
    });
  };

  return (
    <>
      <div className="tabBody">
        <div className="row mb-3">
          <label className="col-3 font-weight-normal col-form-label">
            {translate.Name}:
          </label>
          <div className="col">
            <TextEditor
              onChange={requiredManager.makeRequiredWithId(setName, "textId")}
              defaultValue={name}
              maxLength={STORED_FILTER_NAME_MAX_LENGTH}
            />
            <ValidationMessage
              onChange={requiredManager.makeRequiredWithId(setName, "textId")}
              defaultValue={name}
            />
          </div>
        </div>

        {!isInvited && (
          <div className="row mb-3">
            <label className="col-3 font-weight-normal col-form-label">
              {translate.VisibleTo}:
            </label>
            <div className="col">
              <Dropdown
                value={privateState}
                onChange={setPrivateState}
                items={options}
              />
            </div>
          </div>
        )}

        {isPrivate && (
          <div className="row mb-3">
            <div className="col offset-3">
              <CheckBoxEditor
                value={defaultState}
                onChange={setDefaultState}
                addClass=""
                message={translate.OpenReportWithThisGrouping}
              />
            </div>
          </div>
        )}
      </div>
      <FloatingPanelFooter
        showDelete={!isNew && !isSaving}
        onDeleteClick={confirmDelete}
        isDeleting={isDeleting}
      >
        {!isDeleting ? (
          <button className="btn btn-primary" onClick={save}>
            {translate.Save}
            {isSaving && (
              <i className="fas fa-spinner-third fa-spin third ms-2"></i>
            )}
          </button>
        ) : null}
      </FloatingPanelFooter>
    </>
  );
};

export default StoredGroupingPanel;
